import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import fr from 'facet-rest';
import MetaFormInline from 'Components/UI/Meta/MetaForm/Components/MetaFormInline';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import NavigationWithContext from 'Components/ComponentLibrary/Organisms/NavigationWithContext';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import WhatIfStepNavigator from 'Views/Page/Content/Modal/Components/WhatIfStepNavigator';

const styles = () => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
});

const WhatIfWizardCollectionStepperTemplateContent = ({
  className,
  classes,
  layout,
  metaOverrideSet,
  resource,
  index,
  total,
  finished,
  next,
  previous,
  isStart,
  isEnd,
}) => {
  const { loadModalCollectionByLink, modalCollection } = useModalContext();
  const { onSubmit } = useMetaLayoutContext();
  const [thisStep] = modalCollection;
  const [errors, setErrors] = useState(null);
  const previousLink = thisStep
    .relationship(fr.relationships.previousLink.name)
    ?.link('related')
    .url();

  const onNext = async () => {
    try {
      await resource.submit();
      if (isEnd) {
        await onSubmit(); // leave reppie and go to the next wizard step
        finished();
      } else {
        next(); // go to next rep of reppie
      }
    } catch (e) {
      setErrors(e.errors);
    }
  };

  const onPrevious = async () => {
    if (isStart) {
      await loadModalCollectionByLink(previousLink);
    } else {
      previous();
    }
  };

  return (
    <Box className={classNames(className, classes.root)}>
      <NavigationWithContext />
      <MetaFormInline
        fullWidth
        padding={'none'}
        key={resource.id()}
        noIndividualSubmitButtons
        resource={resource}
        layout={layout}
        metaOverrideSet={metaOverrideSet}
        LayoutProps={{
          index,
          total,
          isStart,
          isEnd,
        }}
        errorsOverride={errors}
      />
      <WhatIfStepNavigator
        onNext={onNext}
        onPrevious={!(isStart && !previousLink) && onPrevious}
      />
    </Box>
  );
};

WhatIfWizardCollectionStepperTemplateContent.defaultProps = {
  className: '',
  metaOverrideSet: undefined,
};

WhatIfWizardCollectionStepperTemplateContent.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  /** layout: The body content layout * */
  layout: PropTypes.object.isRequired,
  /** metaOverrideSet: A meta override set * */
  metaOverrideSet: PropTypes.object,
  /** resource: The displayed resource. Provied by withCollectionStepper HOC * */
  resource: PropTypes.instanceOf(fr.Collection).isRequired,
  /** index: The index of the current resource in the collection. One based numbering (arrayIndex+1). Provied by withCollectionStepper HOC * */
  index: PropTypes.number.isRequired,
  /** total: The total resources in the collection. Provied by withCollectionStepper HOC * */
  total: PropTypes.number.isRequired,
  /** finished: Function to notify stepper to teardown. Provied by withCollectionStepper HOC * */
  finished: PropTypes.func.isRequired,
  /** next: Function to load the next resource. Provied by withCollectionStepper HOC * */
  next: PropTypes.func.isRequired,
  /** previous: Function to load the previous resource. Provied by withCollectionStepper HOC * */
  previous: PropTypes.func.isRequired,
  /** isStart: True if the current resource is the first resource in the collection. Provied by withCollectionStepper HOC * */
  isStart: PropTypes.bool.isRequired,
  /** isEnd: True if the current resource is the last resource in the collection. Provied by withCollectionStepper HOC * */
  isEnd: PropTypes.bool.isRequired,
};

export default withStyles(styles)(WhatIfWizardCollectionStepperTemplateContent);
